import { useCallback, useEffect, useState } from "react";
import { getAuthHeaders, useAuth } from "../contexts/AuthContext";
import type { Match, Player } from "../types";

interface ApiMatch {
	id: string;
	winner_id: string;
	loser_id: string;
	winner_elo_before: number;
	loser_elo_before: number;
	winner_elo_after: number;
	loser_elo_after: number;
	timestamp: number;
}

interface ApiPlayer {
	id: string;
	name: string;
	elo: number;
	wins: number;
	losses: number;
}

function transformMatch(m: ApiMatch): Match {
	return {
		id: m.id,
		winnerId: m.winner_id,
		loserId: m.loser_id,
		winnerEloBefore: m.winner_elo_before,
		loserEloBefore: m.loser_elo_before,
		winnerEloAfter: m.winner_elo_after,
		loserEloAfter: m.loser_elo_after,
		timestamp: m.timestamp,
	};
}

const PAGE_SIZE = 20;

interface AllMatchesProps {
	onBack: () => void;
}

export function AllMatches({ onBack }: AllMatchesProps) {
	const { token, role } = useAuth();
	const isAdmin = role === "admin";
	const [matches, setMatches] = useState<Match[]>([]);
	const [players, setPlayers] = useState<Player[]>([]);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [loading, setLoading] = useState(true);

	const fetchMatches = useCallback(
		async (p: number) => {
			try {
				const headers = getAuthHeaders(token);
				const res = await fetch(
					`/api/matches/all?page=${p}&limit=${PAGE_SIZE}`,
					{ headers },
				);
				const data = await res.json();
				setMatches((data.matches as ApiMatch[]).map(transformMatch));
				setTotalPages(data.totalPages);
				setPage(data.page);
			} catch (e) {
				console.error("Failed to fetch matches:", e);
			} finally {
				setLoading(false);
			}
		},
		[token],
	);

	const handleDelete = async (matchId: string) => {
		if (!window.confirm("Delete this match? Ratings will be recalculated."))
			return;
		try {
			const headers = getAuthHeaders(token);
			const res = await fetch(`/api/matches/${matchId}`, {
				method: "DELETE",
				headers,
			});
			if (res.ok) {
				fetchMatches(page);
			}
		} catch (e) {
			console.error("Failed to delete match:", e);
		}
	};

	useEffect(() => {
		async function fetchPlayers() {
			try {
				const headers = getAuthHeaders(token);
				const res = await fetch("/api/players", { headers });
				const data: ApiPlayer[] = await res.json();
				setPlayers(data);
			} catch (e) {
				console.error("Failed to fetch players:", e);
			}
		}
		fetchPlayers();
	}, [token]);

	useEffect(() => {
		fetchMatches(page);
	}, [fetchMatches, page]);

	const getPlayerName = (id: string) =>
		players.find((p) => p.id === id)?.name ?? "Unknown";

	const formatTime = (timestamp: number) => {
		const date = new Date(timestamp);
		const now = new Date();
		const isToday = date.toDateString() === now.toDateString();
		if (isToday) {
			return date.toLocaleTimeString([], {
				hour: "2-digit",
				minute: "2-digit",
			});
		}
		return date.toLocaleDateString([], {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<div className="full-width">
			<button type="button" onClick={onBack} className="btn-back">
				&larr; Back
			</button>
			<div className="card">
				<h2>All Matches</h2>
				{loading ? (
					<p className="hint">Loading...</p>
				) : matches.length === 0 ? (
					<p className="hint">No matches recorded yet.</p>
				) : (
					<>
						<div className="match-list">
							{matches.map((match) => (
								<div key={match.id} className="match-item">
									<span className="time">{formatTime(match.timestamp)}</span>
									<div className="match-result">
										<span className="winner">
											{getPlayerName(match.winnerId)}
										</span>
										<span className="vs">beat</span>
										<span className="loser">
											{getPlayerName(match.loserId)}
										</span>
									</div>
									<div className="elo-details">
										<span className="elo-player winner">
											{getPlayerName(match.winnerId)}: {match.winnerEloBefore}{" "}
											&rarr; {match.winnerEloAfter}
										</span>
										<span className="elo-player loser">
											{getPlayerName(match.loserId)}: {match.loserEloBefore}{" "}
											&rarr; {match.loserEloAfter}
										</span>
									</div>
									{isAdmin && (
										<button
											type="button"
											className="btn-remove"
											onClick={() => handleDelete(match.id)}
											title="Delete match"
										>
											&times;
										</button>
									)}
								</div>
							))}
						</div>
						{totalPages > 1 && (
							<div className="pagination">
								<button
									type="button"
									onClick={() => setPage((p) => p - 1)}
									disabled={page <= 1}
									className="btn-back"
								>
									&larr; Prev
								</button>
								<span className="pagination-info">
									Page {page} of {totalPages}
								</span>
								<button
									type="button"
									onClick={() => setPage((p) => p + 1)}
									disabled={page >= totalPages}
									className="btn-back"
								>
									Next &rarr;
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
